import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { scaleLinear } from "d3-scale";
import { extent } from "d3-array";

import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";

import { TCatalogEntities } from "types";

import Pipeline from "../Pipeline";
import Filter from "../../Filter";

import LeistungReducer from "./LeistungReducer";
import LeistungIndicator from "./LeistungIndicator";
import { Typography } from "@mui/material";

interface IFilter {
  entities: TCatalogEntities;
  value: number[];
  dispatchFilter?: any;
}

export const SideFilter: React.FC<IFilter> = ({
  entities: { vehicle },
  value = [],
  dispatchFilter = () => null,
}): JSX.Element | null => {
  const [maximum, setMaximum]: [number | undefined, any] = useState();
  const [minimum, setMinimum]: [number | undefined, any] = useState();
  const [range, setRange]: [[number, number] | undefined, any] = useState();

  const { formatMessage: t } = useIntl();

  React.useEffect(() => {
    if (!vehicle) return;
    const range: [any, any] = extent(
      Object.values(vehicle)
        .map((e: any) => e.kw)
        .filter((e: any) => !!e),
    );
    setRange(range);
  }, [vehicle, dispatchFilter]);

  React.useEffect(
    () => {
      if (!(vehicle && range)) return;
      if (!value) {
        setMinimum(range[0]);
        setMaximum(range[1]);
      } else {
        setMinimum(value[0]);
        setMaximum(value[1]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, range],
  );

  const handleChange = (element: any, newValue: any) => {
    setMinimum(newValue[0]);
    setMaximum(newValue[1]);
  };

  const submit = (element: any, value: any) => {
    const newValue =
      JSON.stringify(range) === JSON.stringify(value) ? null : value;
    dispatchFilter({
      type: "UPDATE_FILTER",
      payload: { leistung: newValue },
    });
  };

  if (!(minimum && maximum && range)) return null;

  const marks = [
    { value: range[0], label: range[0] },
    { value: range[1], label: range[1] },
  ];

  if (minimum > range[0]) marks.push({ value: minimum, label: minimum });
  if (maximum < range[1] && minimum !== maximum)
    marks.push({ value: maximum, label: maximum });

  const options = {
    min: range[0],
    max: range[1],
    onChange: handleChange,
    onChangeCommitted: submit,
    marks,
  };

  const Component = (
    <Box sx={{ px: 1 }}>
      <Box sx={{ textAlign: "right" }}>
        <Typography variant="body1">in kW</Typography>
      </Box>
      <Slider
        getAriaLabel={(i) => i.toString()}
        value={[minimum, maximum]}
        {...options}
      />
    </Box>
  );

  return (
    <Filter
      component={Component}
      open={false}
      title={t({ id: "filter.leistung.label", defaultMessage: "Leistung" })}
    />
  );
};

export const SimpleFilter: React.FC<IFilter> = ({
  entities: { vehicle: vehicles },
  value = [],
  dispatchFilter = () => null,
}): JSX.Element | null => {
  const { formatMessage: t, formatNumber: n } = useIntl();
  const [range, setRange]: [[number, number] | undefined, any] = useState();

  React.useEffect(() => {
    if (!vehicles) return;
    const edges: [any, any] = extent(Object.values(vehicles), (v) => v.kw);
    const scale = scaleLinear().domain(edges).range([0, 1]).nice();
    const range = [scale.invert(0), scale.invert(1)];
    setRange(range);
  }, [vehicles]);

  if (!range) return null;

  const setValueHandler = (event: SelectChangeEvent) => {
    // Check if all variants are selected
    const value = parseInt(event.target.value);
    const newValue = value === 0 ? null : [value, range[1]];
    dispatchFilter({ type: "UPDATE_FILTER", payload: { leistung: newValue } });
  };

  var options = [];
  for (var i = range[0] / 25; i <= range[1] / 25; i++) {
    options.push({
      value: i * 25,
      title: `${n(i * 25)}kW (${Math.floor(i * 25 * 1.35962)}PS)`,
    });
  }
  options.shift();

  const all = {
    title: t({ id: "common.alle", defaultMessage: "Alle" }),
  };

  return (
    <FormControl size="small">
      <InputLabel id="filter-kw-simple-label">
        <FormattedMessage id="filter.kw-to.label" defaultMessage="ab kW" />
      </InputLabel>
      <Select
        labelId="filter-kw-simple-label"
        id="filter-kw-simple"
        value={!value ? "0" : value[0].toString()}
        label={t({
          id: "filter.kw-to.label",
          defaultMessage: "ab kW",
        })}
        onChange={setValueHandler}
      >
        <MenuItem value={0}>{all.title}</MenuItem>
        {options.map(({ value, title }) => (
          <MenuItem key={"kw-" + value} value={value}>
            {title}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

interface IContainer {
  Pipe?: any;
  variant?: string;
}

const Container: React.FC<IContainer> = ({
  Pipe = Pipeline,
  variant = "side",
}) => {
  const Filter = variant === "side" ? SideFilter : SimpleFilter;
  return (
    <Pipe
      {...{
        type: "leistung",
        defaultValue: null,
        Component: Filter,
        Reducer: LeistungReducer,
        Indicator: LeistungIndicator,
      }}
    />
  );
};

export default Container;
